import { lazy, Suspense } from "react";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Index from "./pages/Index";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Lazy load non-critical pages
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const DealerApplication = lazy(() => import("./pages/DealerApplication"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const SalesRepApplication = lazy(() => import("./pages/SalesRepApplication"));
const AdminShell = lazy(() => import("./pages/admin/AdminShell"));

import { CartProvider } from "@/context/CartContext";
import { useLocation } from "react-router-dom";
import { useEffect } from "react";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <CartProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner position="top-center" />
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center bg-white">
                <div className="w-12 h-12 rounded-2xl bg-[#006C73] animate-pulse" />
              </div>
            }>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/careers" element={<CareersPage />} />
                <Route path="/dealer-application" element={<DealerApplication />} />
                <Route path="/sales-rep-application" element={<SalesRepApplication />} />
                <Route path="/admin" element={<AdminShell />} />
                <Route path="/admin/*" element={<AdminShell />} />
                <Route path="/Admin" element={<Navigate to="/admin" replace />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <FloatingWhatsApp />
          </BrowserRouter>
        </TooltipProvider>
      </CartProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
